import {SourceImage} from "./RcpRecipe";
import {FoodEntry} from "./FoodEntry";

export class CombinedFoodIngredient {
    mainFoodEntryId: number;
    ingredientEntryId: number;
    ratio: number;
    internalId: number;

    /**
     * Construct a combined food ingredient
     * @param mainFoodEntryId {number} the main food entry this should always be the combined food item it is linked to
     * @param ingredientEntryId {number} the food entry
     * @param ratio {number} the amount
     * @param internalId {number} optional internal id for UI internal purposes.
     */
    constructor(mainFoodEntryId: number, ingredientEntryId: number, ratio: number, internalId: number = 0) {
        this.mainFoodEntryId = mainFoodEntryId;
        this.ingredientEntryId = ingredientEntryId;
        this.ratio = ratio;
        this.internalId = internalId
    }

    /**
     * Converts the current object properties to a JSON representation.
     *
     * @return {Object} A JSON object
     */
    toJson(): any {
        return {
            foodEntryId: this.mainFoodEntryId,
            ingFoodEntryId: this.ingredientEntryId,
            ratio: this.ratio
        }
    }

    /**
     * Return the object form from the json data.
     * @param jsonData
     * @returns {CombinedFoodIngredient}
     */
    static fromJson(jsonData: any): CombinedFoodIngredient {
        return new CombinedFoodIngredient(
            jsonData.foodEntryId,
            jsonData.ingFoodEntryId,
            jsonData.ratio);
    }
}

/**
 * A Combined food entry is based on a regular food entry, but it also has ingredient entries. Each ingredient is
 * a ratio of the overall amount. This is only used during editing as the nutritional value of the ingredients is
 * stored in the main entry.
 */
export class CombinedFoodEntry {
    static EMPTY = new CombinedFoodEntry(FoodEntry.EMPTY, []);

    foodEntry: FoodEntry;
    ingredients: CombinedFoodIngredient[];

    /**
     * @param foodEntry {FoodEntry} the actual combined item
     * @param ingredients {[CombinedFoodIngredient]} the list of ingredients with their ratios
     */
    constructor(foodEntry: FoodEntry, ingredients: CombinedFoodIngredient[]) {
        this.foodEntry = foodEntry;
        this.ingredients = ingredients;
    }

    /**
     * Convert the object form to Json
     * @returns {{foodEntry: *, ingredients: *}}
     */
    toJson(): any {
        return {
            foodEntry: this.foodEntry.toJson(),
            ingredients: this.ingredients.map(ing => ing.toJson())
        }
    }

    /**
     * Convert json into object form
     * @param jsonData the input data
     * @returns {CombinedFoodEntry}
     */
    static fromJson(jsonData: any): CombinedFoodEntry {
        return new CombinedFoodEntry(
            FoodEntry.fromJson(jsonData.foodEntry),
            jsonData.ingredients.map(CombinedFoodIngredient.fromJson)
        );
    }
}